import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import Cookies from 'js-cookie';

// Интерфейсы для типизации
interface User {
    id: number;
    email: string;
    username?: string;
    // Добавьте другие поля пользователя по необходимости
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    loading: boolean;
    login: (email: string, password: string) => Promise<any>;
    register: (email: string, password: string, username: string, password_confirm: string) => Promise<any>;
    logout: () => void;
    refreshUser: () => Promise<User>;
}

interface AuthProviderProps {
    children: ReactNode;
}

// Создаем контекст с типизацией
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({children}) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const token = Cookies.get('access_token');
        setIsAuthenticated(!!token);

        if (token) {
            fetchUserData();
        } else {
            setLoading(false);
        }
    }, []);

    const fetchUserData = async (): Promise<User> => {
        try {
            const accessToken = Cookies.get('access_token');

            if (!accessToken) {
                throw new Error('Требуется авторизация');
            }

            let apiUrl = process.env.REACT_APP_API_BASE || '/api';
            const response = await fetch(`${apiUrl}/user/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    Cookies.remove('access_token');
                    Cookies.remove('refresh_token');
                    setIsAuthenticated(false);
                    setUser(null);
                }
                throw new Error('Ошибка при получении данных пользователя');
            }

            const userData: User = await response.json();
            setUser(userData);
            setIsAuthenticated(true);
            console.log('User data:', user);
            return userData;
        } catch (err) {
            console.error('Ошибка загрузки пользователя:', err);
            setUser(null);
            setIsAuthenticated(false);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const login = async (email: string, password: string): Promise<any> => {
        try {
            let apiUrl = process.env.REACT_APP_API_BASE || '/api';
            console.log(apiUrl);
            const response = await fetch(`${apiUrl}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({email, password}),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Ошибка авторизации');
            }

            const data = await response.json();
            Cookies.set('access_token', data.access_token, {expires: 1});
            Cookies.set('refresh_token', data.refresh_token, {expires: 7});

            // Получаем данные пользователя сразу после входа
            const userData = await fetchUserData();

            return data;
        } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
            throw error;
        }
    };

    const register = async (
        email: string,
        password: string,
        username: string,
        password_confirm: string
    ): Promise<any> => {
        try {
            let apiUrl = process.env.REACT_APP_API_BASE || '/api';
            const response = await fetch(`${apiUrl}/auth/registration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({email, password, username, password_confirm}),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Ошибка регистрации');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            throw error;
        }
    };

    const logout = (): void => {
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        setUser(null);
        setIsAuthenticated(false);
    };

    const value: AuthContextType = {
        user,
        isAuthenticated,
        loading,
        login,
        register,
        logout,
        refreshUser: fetchUserData,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};